import React, { useState, useRef, useCallback } from 'react';
import { useLazyQuery, gql } from '@apollo/client';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

const SEARCH_SUGGESTIONS_QUERY = gql`
  query SearchSuggestions($prefix: String!) {
    searchSuggestions(prefix: $prefix) {
      text
      value
      category
    }
  }
`;

export interface SearchSuggestion {
  text: string;
  value: string;
  category: string;
}

interface SearchSuggestionsData {
  searchSuggestions: SearchSuggestion[];
}

interface SearchSuggestionsVariables {
  prefix: string;
}

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${({ theme }) => theme?.colors?.cardBg ?? '#fff'};
  border-radius: 0 0 14px 14px;
  box-shadow: ${({ theme }) =>
    theme?.shadows?.elevated ?? '0 8px 32px rgba(26, 54, 93, 0.18)'};
  max-height: 360px;
  overflow-y: auto;
  margin-top: 2px;
`;

const CategoryLabel = styled.div`
  padding: 10px 16px 4px;
  font-size: 0.7rem;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.textSecondary ?? '#8b9dc3'};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const SuggestionItem = styled.div<{ $active?: boolean }>`
  padding: 10px 16px;
  cursor: pointer;
  color: ${({ theme }) => theme?.colors?.text ?? '#1a365d'};
  font-size: 0.95rem;
  background: ${({ $active, theme }) =>
    $active ? (theme?.colors?.surfaceHover ?? '#f0f4f8') : 'transparent'};
  transition: background 0.1s;
  display: flex;
  align-items: center;
  gap: 10px;
  &:hover {
    background: ${({ theme }) => theme?.colors?.surfaceHover ?? '#f0f4f8'};
  }
  &:last-child {
    border-radius: 0 0 14px 14px;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme?.colors?.textSecondary ?? '#8b9dc3'};
`;

const CATEGORY_MESSAGES: Record<string, string> = {
  popular: 'suggestions.popular',
  tags: 'suggestions.tags',
  newspapers: 'suggestions.newspapers',
};

interface CategoryIconProps {
  category: string;
}

const CategoryIcon = ({ category }: CategoryIconProps) => {
  if (category === 'popular') {
    return (
      <IconWrapper>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      </IconWrapper>
    );
  }
  if (category === 'tags') {
    return (
      <IconWrapper>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
          <circle cx="7" cy="7" r="1" />
        </svg>
      </IconWrapper>
    );
  }
  if (category === 'newspapers') {
    return (
      <IconWrapper>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="3" width="20" height="18" rx="2" />
          <path d="M8 7h8M8 11h8M8 15h4" />
        </svg>
      </IconWrapper>
    );
  }
  return null;
};

interface SuggestionGroup {
  category: string;
  items: SearchSuggestion[];
}

function groupByCategory(suggestions: SearchSuggestion[]): SuggestionGroup[] {
  const groups: SuggestionGroup[] = [];
  const seen = new Set<string>();
  for (const s of suggestions) {
    if (!seen.has(s.category)) {
      seen.add(s.category);
      groups.push({ category: s.category, items: [] });
    }
    groups.find((g) => g.category === s.category)!.items.push(s);
  }
  return groups;
}

export interface UseSearchSuggestionsOptions {
  onSelect: (suggestion: SearchSuggestion) => void;
}

export interface UseSearchSuggestionsReturn {
  suggestions: SearchSuggestion[];
  isOpen: boolean;
  activeIndex: number;
  handleInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleSelect: (suggestion: SearchSuggestion) => void;
  handleBlur: () => void;
  handleFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export function useSearchSuggestions({
  onSelect,
}: UseSearchSuggestionsOptions): UseSearchSuggestionsReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [fetchSuggestions, { data }] = useLazyQuery<
    SearchSuggestionsData,
    SearchSuggestionsVariables
  >(SEARCH_SUGGESTIONS_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const suggestions = data?.searchSuggestions ?? [];

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.trim();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setActiveIndex(-1);
      if (val.length >= 2) {
        debounceRef.current = setTimeout(() => {
          fetchSuggestions({ variables: { prefix: val } });
          setIsOpen(true);
        }, 250);
      } else {
        setIsOpen(false);
      }
    },
    [fetchSuggestions]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || suggestions.length === 0) {
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, -1));
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        onSelect(suggestions[activeIndex]);
        setIsOpen(false);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [isOpen, suggestions, activeIndex, onSelect]
  );

  const handleSelect = useCallback(
    (suggestion: SearchSuggestion) => {
      onSelect(suggestion);
      setIsOpen(false);
    },
    [onSelect]
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => setIsOpen(false), 150);
  }, []);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const val = e.target.value.trim();
      if (val.length >= 2 && suggestions.length > 0) {
        setIsOpen(true);
      }
    },
    [suggestions]
  );

  return {
    suggestions,
    isOpen,
    activeIndex,
    handleInput,
    handleKeyDown,
    handleSelect,
    handleBlur,
    handleFocus,
  };
}

export interface SuggestionsDropdownProps {
  suggestions: SearchSuggestion[];
  isOpen: boolean;
  activeIndex: number;
  onSelect: (suggestion: SearchSuggestion) => void;
}

export function SuggestionsDropdown({
  suggestions,
  isOpen,
  activeIndex,
  onSelect,
}: SuggestionsDropdownProps) {
  const intl = useIntl();

  if (!isOpen || !suggestions || suggestions.length === 0) return null;

  const groups = groupByCategory(suggestions);
  let itemIndex = 0;

  return (
    <Dropdown role="listbox" id="search-suggestions-listbox">
      {groups.map((group) => (
        <React.Fragment key={group.category}>
          <CategoryLabel>
            {intl.formatMessage({
              id: CATEGORY_MESSAGES[group.category] ?? group.category,
            })}
          </CategoryLabel>
          {group.items.map((item) => {
            const currentIndex = itemIndex++;
            return (
              <SuggestionItem
                key={`${item.category}-${item.text}`}
                role="option"
                aria-selected={currentIndex === activeIndex}
                $active={currentIndex === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(item);
                }}
              >
                <CategoryIcon category={item.category} />
                {item.text}
              </SuggestionItem>
            );
          })}
        </React.Fragment>
      ))}
    </Dropdown>
  );
}
